/**
 * GHL Dashboard types, cost calculations, and formatting utilities.
 */

// --- Types ---

export interface GHLAnalysis {
  readonly id: string
  readonly location_id: string
  readonly contact_id: string
  readonly conversation_id: string | null
  readonly opportunity_id: string | null
  readonly trigger_event: string
  readonly booking_detected: boolean
  readonly booking_confidence: number
  readonly price_quoted: number | null
  readonly price_confirmed: boolean
  readonly extracted_fields: Record<string, unknown>
  readonly conversation_summary: string | null
  readonly recommended_action: string
  readonly reasoning: string | null
  readonly action_executed: boolean
  readonly action_result: Record<string, unknown> | null
  readonly message_count: number
  readonly trigger_message_id: string | null
  readonly input_tokens: number | null
  readonly output_tokens: number | null
  readonly model_used: string | null
  readonly event_date: string | null
  readonly urgency_level: string | null
  readonly tags_applied: string[] | null
  readonly raw_messages: unknown[] | null
  readonly raw_claude_response: string | null
  readonly whisper_duration_seconds: number | null
  readonly whisper_audio_bytes: number | null
  readonly whisper_call_count: number | null
  readonly created_at: string
  readonly updated_at: string
  // --- Present only on rows returned by `get_ghl_activity_feed_grouped` ---
  // Total number of analyses in the conversation|contact group.
  readonly sibling_count?: number
  // Distinct opportunity counts per terminal state, derived from each
  // opportunity's most recent STAGE_CHANGE event. Opportunities with no stage
  // change yet are NOT counted; an empty row (all zero) displays as "—".
  readonly booked_count?: number
  readonly open_count?: number
  readonly lost_count?: number
}

export interface GHLDashboardStats {
  readonly total_analyses: number
  readonly bookings_detected: number
  readonly actions_executed: number
  readonly total_input_tokens: number
  readonly total_output_tokens: number
  readonly avg_confidence: number
  readonly total_price_quoted: number
  readonly total_whisper_duration: number
  readonly total_whisper_calls: number
  readonly by_action: Record<string, number>
  readonly by_location: readonly {
    readonly location_id: string
    readonly count: number
    readonly bookings: number
    readonly input_tokens: number
    readonly output_tokens: number
  }[]
}

export interface GHLDailyStats {
  readonly date: string
  readonly total: number
  readonly bookings: number
  readonly input_tokens: number
  readonly output_tokens: number
  readonly avg_confidence: number
}

export interface GHLQueueStatus {
  readonly active: number
  readonly queued: number
  readonly maxConcurrent: number
  readonly pendingTimers: number
  readonly cooldownCacheSize: number
}

export interface GHLLocationSummary {
  readonly location_id: string
  readonly count: number
  readonly name?: string
}

export interface GHLLocationOverview {
  readonly location_id: string
  readonly name: string
  readonly analysis_count: number
  readonly bookings_detected: number
  readonly actions_executed: number
  readonly total_input_tokens: number
  readonly total_output_tokens: number
  readonly avg_confidence: number
  readonly last_analysis_at: string | null
  readonly pending_followups: number
  // CRM Automation master toggle surfaced from ghl_location_settings via the
  // get_ghl_location_overview RPC. Defaults FALSE for new installs (existing
  // installs were backfilled with explicit TRUE in the master-toggles
  // migration). The data_pipeline toggle is no longer middleware-owned and
  // is read by the Qualifier app instead.
  readonly crm_automation_enabled: boolean
}

// --- Follow-Up Schedule Types ---

export interface GHLFollowUpRow {
  readonly id: string
  readonly location_id: string
  readonly opportunity_id: string
  readonly contact_id: string
  readonly step_number: number
  readonly phase: number
  readonly step_label: string
  readonly fire_at: string
  readonly quote_date: string | null
  readonly pickup_date: string | null
  readonly status: string
  readonly skip_reason: string | null
  readonly sms_message: string | null
  readonly sms_prompt_context: string | null
  readonly conversation_id: string | null
  readonly message_id: string | null
  readonly error_message: string | null
  readonly attempt_count: number
  readonly last_attempt_at: string | null
  readonly next_retry_at: string | null
  readonly input_tokens: number | null
  readonly output_tokens: number | null
  readonly model_used: string | null
  readonly created_at: string
  readonly updated_at: string
}

/** A single opportunity's follow-up summary (one row per opportunity, from grouped RPC). */
export interface GHLFollowUpGroupedRow {
  readonly id: string
  readonly location_id: string
  readonly opportunity_id: string
  readonly contact_id: string
  readonly step_number: number
  readonly phase: number
  readonly step_label: string
  readonly fire_at: string
  readonly quote_date: string | null
  readonly pickup_date: string | null
  readonly status: string
  readonly skip_reason: string | null
  readonly sms_message: string | null
  readonly error_message: string | null
  readonly input_tokens: number | null
  readonly output_tokens: number | null
  readonly model_used: string | null
  readonly created_at: string
  // Group metadata
  readonly total_steps: number
  readonly pending_steps: number
  readonly sent_steps: number
  readonly cancelled_steps: number
  readonly failed_steps: number
  // --- Intent-gate audit fields (NULL when the gate did not run) ---
  // Note: DB value is 'send' | 'skip' (matches the gate's decision union).
  // The dashboard label is "skipped" for visual presentation only.
  readonly gate_decision: 'send' | 'skip' | null
  readonly gate_reason: string | null
  readonly gate_evaluated_at: string | null
}

export interface GHLFollowUpStats {
  readonly total_scheduled: number
  readonly pending: number
  readonly processing: number
  readonly sent: number
  readonly failed: number
  readonly cancelled: number
  readonly skipped: number
  readonly total_input_tokens: number
  readonly total_output_tokens: number
  readonly upcoming_24h: number
  readonly upcoming_2h: number
  readonly by_phase: Record<string, number>
  readonly by_step: readonly {
    readonly step_number: number
    readonly step_label: string
    readonly total: number
    readonly sent: number
    readonly pending: number
  }[]
}

// --- Claude Pricing Constants ---

// Keyed by the `model_used` value stored on each row, so historical rows keep
// pricing at the model that actually produced them. Never prune entries when
// migrating models — old rows still reference the old IDs.
const CLAUDE_PRICING: Record<string, { input: number; output: number }> = {
  'claude-opus-5-5': { input: 4.0 / 1_000_000, output: 20.0 / 1_000_000 },
  'claude-sonnet-5': { input: 2.0 / 1_000_000, output: 10.0 / 1_000_000 },
  'claude-sonnet-4-6-20260101': { input: 3.0 / 1_000_000, output: 15.0 / 1_000_000 },
  'claude-sonnet-4-20250514': { input: 3.0 / 1_000_000, output: 15.0 / 1_000_000 },
  'claude-opus-4-20250514': { input: 15.0 / 1_000_000, output: 75.0 / 1_000_000 },
  'claude-haiku-3-20240307': { input: 0.25 / 1_000_000, output: 1.25 / 1_000_000 },
  'claude-haiku-4-5-20251001': { input: 0.80 / 1_000_000, output: 4.0 / 1_000_000 },
}

// Used when a row has no `model_used`, and by the aggregate RPCs, which sum
// tokens without a model breakdown. Tracks the model currently in production so
// the common case is right; mixed-model date ranges are approximate until the
// aggregates group by model_used.
const DEFAULT_PRICING = { input: 4.0 / 1_000_000, output: 20.0 / 1_000_000 }

// Whisper pricing: $0.003/min for gpt-4o-mini-transcribe
const WHISPER_PRICE_PER_SECOND = 0.003 / 60

// --- Cost Calculation ---

export function calculateWhisperCost(durationSeconds: number | null): number {
  if (!durationSeconds) return 0
  return durationSeconds * WHISPER_PRICE_PER_SECOND
}

export function calculateAnalysisCost(
  inputTokens: number | null,
  outputTokens: number | null,
  model: string | null
): number {
  if (!inputTokens && !outputTokens) return 0
  const pricing = (model ? CLAUDE_PRICING[model] : null) ?? DEFAULT_PRICING
  return (inputTokens ?? 0) * pricing.input + (outputTokens ?? 0) * pricing.output
}

export function calculateTotalCost(
  totalInputTokens: number,
  totalOutputTokens: number,
  model?: string
): number {
  const pricing = (model ? CLAUDE_PRICING[model] : null) ?? DEFAULT_PRICING
  return totalInputTokens * pricing.input + totalOutputTokens * pricing.output
}

// --- Formatting ---

export function formatCost(cost: number): string {
  if (cost < 0.01) return '<$0.01'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(cost)
}

export function formatTokenCount(count: number | null): string {
  if (count === null || count === 0) return '--'
  if (count < 1000) return String(count)
  if (count < 1_000_000) return `${(count / 1000).toFixed(1)}K`
  return `${(count / 1_000_000).toFixed(2)}M`
}

export function formatRelativeTime(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then

  if (diffMs < 0) return 'just now'

  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return `${seconds}s ago`

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function truncateId(id: string, length = 8): string {
  if (id.length <= length) return id
  return `${id.slice(0, length)}...`
}

// --- Action Colors ---

export const ACTION_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  MOVE_TO_BOOKED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Booked' },
  UPDATE_FIELDS_ONLY: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Fields Updated' },
  MARK_LOST: { bg: 'bg-rose-100', text: 'text-rose-800', label: 'Marked Lost' },
  CREATE_OPPORTUNITY: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Created Opp' },
  NO_ACTION: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'No Action' },
}

export function getActionColor(action: string) {
  return ACTION_COLORS[action] ?? { bg: 'bg-gray-100', text: 'text-gray-600', label: action }
}

// --- Toggle Suppression ---
//
// Maps the SkipReason values written by the middleware (see
// apps/ghl-middleware/src/types/analysis.ts) to dashboard-facing labels.
// New reasons added in middleware should be added here too — unknown reasons
// fall through to a generic "Skipped" label so old/new builds interop.
export const SKIP_REASON_LABELS: Record<string, string> = {
  booked_disabled: 'Booked stage disabled',
  won_disabled: 'Won status disabled',
  lost_disabled: 'Lost status disabled',
  fields_disabled: 'Field updates disabled',
  quoted_disabled: 'Quoted stage disabled',
  followup_sms_disabled: 'Follow-up SMS disabled',
  payment_required_not_confirmed: 'Payment not confirmed',
  mark_lost_in_grace_period: 'Mark-lost deferred (grace window)',
}

export function getSkipReasonLabel(reason: string): string {
  return SKIP_REASON_LABELS[reason] ?? reason
}

/**
 * Pull the `skippedByToggle` array out of an `action_result` JSON blob.
 *
 * The DB stores `action_result` as `Record<string, unknown> | null` (the
 * middleware writes the full `ActionResult`). This helper isolates the
 * ugly-cast in one place so call sites can render the suppression list.
 */
export function getSkippedByToggle(actionResult: Record<string, unknown> | null | undefined): readonly string[] {
  if (!actionResult || typeof actionResult !== 'object') return []
  const raw = (actionResult as { skippedByToggle?: unknown }).skippedByToggle
  if (!Array.isArray(raw)) return []
  return raw.filter((v): v is string => typeof v === 'string')
}

// --- Follow-Up Status Colors ---

export const FOLLOWUP_STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Pending' },
  processing: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Processing' },
  sent: { bg: 'bg-green-100', text: 'text-green-800', label: 'Sent' },
  failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Cancelled' },
  skipped: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Skipped' },
}

export function getFollowUpStatusColor(status: string) {
  return FOLLOWUP_STATUS_COLORS[status] ?? { bg: 'bg-gray-100', text: 'text-gray-600', label: status }
}

/** Format a step number + label into a short display string (e.g., "#1 (1h)"). */
export function formatStepBadge(stepNumber: number, stepLabel: string): string {
  if (stepLabel === 'mark_lost') return '#10 (lost)'
  // Decline-grace mark_lost: out-of-band step (99) inserted on demand by
  // executeMarkLost when the location has a grace window configured.
  // Dashboard label distinguishes it from the post-pickup mark_lost step.
  if (stepLabel === 'mark_lost_grace') return 'grace (lost)'
  const shortLabel = stepLabel.replace('_after_quote', '')
  return `#${stepNumber} (${shortLabel})`
}

/**
 * Format a future fire_at date as relative time (e.g., "in 2h", "in 3d").
 * Falls back to formatRelativeTime for past dates.
 */
export function formatFireAt(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = then - now

  if (diffMs <= 0) return formatRelativeTime(dateStr)

  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return `in ${seconds}s`

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `in ${minutes}m`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `in ${hours}h`

  const days = Math.floor(hours / 24)
  return `in ${days}d`
}

// --- Date Range Helpers ---

export function getDateRange(preset: string): { start: Date; end: Date } {
  const end = new Date()
  const start = new Date()

  switch (preset) {
    case '7d':
      start.setDate(start.getDate() - 7)
      break
    case '14d':
      start.setDate(start.getDate() - 14)
      break
    case '30d':
      start.setDate(start.getDate() - 30)
      break
    case '90d':
      start.setDate(start.getDate() - 90)
      break
    default:
      start.setDate(start.getDate() - 30)
  }

  return { start, end }
}
